/**
 * Declarative tool & settings model — the single source of truth a tool ships.
 * See features/tool_settings_framework.md.
 *
 * The whole point: a tool is DATA. Its scope is two knobs (`levels`,
 * `perMode`); its settings UI is a list of `Field`s. The shared engine
 * (useScopedSetting) + panel (SettingsPanel) + field renderer (FieldList)
 * interpret this — so adding a tool or re-scoping it is a one-line edit, never
 * a new dialog.
 *
 * Phase 1 seeds only Price Range; the line tools / Fibonacci / Chart Properties
 * migrate in later phases.
 */
import type { LineStyleName } from "@/state/slices/uiSlice";
import { PRICE_RANGE_DEFAULT_COLOR } from "@/components/Chart/priceRangeRenderer";

/** Which scope a setting is being edited at. */
export type ScopeLevel = "workspace" | "user";

export interface ScopeConfig {
  /** Where the setting can live:
   *   - "tab"            → per-tab (workspace) only, no scope selector
   *   - "user"           → user default only, no scope selector
   *   - "workspace-user" → 2-scope cascade (tab override → user default),
   *                        shows the Scope dropdown + inherit/Customize/Remove */
  levels: "tab" | "user" | "workspace-user";
  /** Store separate values per light/dark app mode (true for colour-bearing
   *  settings), or one shared value (false). */
  perMode: boolean;
}

// --- field kinds (the body of a settings panel) ---

interface BaseField {
  /** The settings/ChartTheme key this control reads + writes. */
  key: string;
  label: string;
}
export interface ColorField extends BaseField {
  kind: "color";
  default?: string;
  /** Optional companion boolean key: when true this colour follows/"matches"
   *  another (e.g. label follows box). Rendered as a checkbox; the picker is
   *  shown only when unmatched. */
  matchKey?: string;
  matchLabel?: string;
}
export interface NumberField extends BaseField {
  kind: "number";
  min: number;
  max: number;
  step?: number;
  unit?: string;
  default?: number;
}
export interface StyleField extends BaseField {
  kind: "style"; // LineStyleName dropdown + dash preview
  default?: LineStyleName;
}
export interface ToggleField extends BaseField {
  kind: "toggle";
  default?: boolean;
}
export interface SelectField extends BaseField {
  kind: "select";
  options: { value: string; label: string }[];
  default?: string;
}
/** Segmented radio-pill row (Left | Right style) — same widget the Fib dialog
 *  uses for label side. Best for 2–3 mutually-exclusive options. */
export interface SegmentedField extends BaseField {
  kind: "segmented";
  options: { value: string; label: string }[];
  default?: string;
}
/** A non-interactive section heading that groups the fields below it (Box /
 *  Labels / Midline …). `key` is just a unique React id. */
export interface HeaderField extends BaseField {
  kind: "header";
}
export type Field =
  | ColorField
  | NumberField
  | StyleField
  | ToggleField
  | SelectField
  | SegmentedField
  | HeaderField;

export interface ToolSettings {
  scope: ScopeConfig;
  fields: Field[];
}

export interface ToolDef {
  id: string;
  label: string;
  /** Where the drawing array lives in `uiSlice` + its workspace wire key.
   *  Drives the registry-driven persistence (later phase). */
  drawing?: { stateKey: string; wireKey: string };
  settings?: ToolSettings;
}

// --- tool definitions ---

export const PRICE_RANGE_TOOL: ToolDef = {
  id: "priceRange",
  label: "Price range",
  drawing: { stateKey: "priceRangeBoxes", wireKey: "price_ranges" },
  settings: {
    scope: { levels: "workspace-user", perMode: true },
    fields: [
      { kind: "header", key: "hdr-box", label: "Box" },
      {
        kind: "color",
        key: "priceRangeColor",
        label: "Colour",
        default: PRICE_RANGE_DEFAULT_COLOR,
      },
      {
        kind: "number",
        key: "priceRangeBoxWidth",
        label: "Thickness",
        min: 1,
        max: 4,
        unit: "px",
        default: 2,
      },

      { kind: "header", key: "hdr-labels", label: "Labels" },
      {
        kind: "color",
        key: "priceRangeTextColor",
        label: "Colour",
        matchKey: "priceRangeTextMatch",
        matchLabel: "Match box",
        default: PRICE_RANGE_DEFAULT_COLOR,
      },
      {
        kind: "segmented",
        key: "priceRangeLabelSide",
        label: "Side",
        options: [
          { value: "left", label: "Left" },
          { value: "right", label: "Right" },
        ],
        default: "right",
      },
      {
        kind: "segmented",
        key: "priceRangeLabelWeight",
        label: "Weight",
        options: [
          { value: "normal", label: "Normal" },
          { value: "bold", label: "Bold" },
        ],
        default: "bold",
      },

      { kind: "header", key: "hdr-arrow", label: "Arrow" },
      {
        kind: "toggle",
        key: "priceRangeArrow",
        label: "Show",
        default: true,
      },

      { kind: "header", key: "hdr-midline", label: "Midline (pullback)" },
      {
        kind: "toggle",
        key: "priceRangeMidline",
        label: "Show",
        default: true,
      },
      {
        kind: "style",
        key: "priceRangeMidlineStyle",
        label: "Style",
        default: "dashed",
      },
      {
        kind: "number",
        key: "priceRangeMidlineWidth",
        label: "Thickness",
        min: 1,
        max: 4,
        unit: "px",
        default: 1,
      },
    ],
  },
};

// Line tools (vertical / horizontal / trend / Elliott wave) share the same
// colour + thickness + style shape. Per-tab (workspace) scope, per app mode —
// matching their current behaviour. Promote to "workspace-user" later by
// editing the one `scope` line (the whole point of the framework).
function lineTool(
  id: string,
  label: string,
  colorKey: string,
  widthKey: string,
  styleKey: string,
  colorDefault: string,
  widthDefault: number,
  styleDefault: LineStyleName,
): ToolDef {
  return {
    id,
    label,
    settings: {
      scope: { levels: "workspace-user", perMode: true },
      fields: [
        { kind: "color", key: colorKey, label: "Colour", default: colorDefault },
        {
          kind: "number",
          key: widthKey,
          label: "Thickness",
          min: 1,
          max: 4,
          unit: "px",
          default: widthDefault,
        },
        { kind: "style", key: styleKey, label: "Style", default: styleDefault },
      ],
    },
  };
}

export const VERTICAL_TOOL = lineTool(
  "vertical", "Vertical line", "vlineColor", "vlineWidth", "vlineStyle",
  "#FF9800", 1, "dashed",
);
export const HORIZONTAL_TOOL: ToolDef = (() => {
  const tool = lineTool(
    "horizontal", "Horizontal line", "hlineColor", "hlineWidth", "hlineStyle",
    "#2962FF", 1, "dashed",
  );
  // Optional text label. These are the type-level DEFAULTS (colour +
  // placement); each can be overridden per-line from the contextual toolbar.
  tool.settings!.fields.push(
    { kind: "header", key: "hdr-hline-text", label: "Text label" },
    {
      kind: "color",
      key: "hlineTextColor",
      label: "Colour",
      default: "#2962FF",
    },
    {
      kind: "segmented",
      key: "hlineTextHPos",
      label: "Side",
      options: [
        { value: "left", label: "Left" },
        { value: "right", label: "Right" },
      ],
      default: "left",
    },
    {
      kind: "segmented",
      key: "hlineTextVPos",
      label: "Position",
      options: [
        { value: "above", label: "Above" },
        { value: "below", label: "Below" },
      ],
      default: "above",
    },
  );
  return tool;
})();
export const TREND_TOOL = lineTool(
  "diagonal", "Trend line", "trendColor", "trendWidth", "trendStyle",
  "#2962FF", 1, "solid",
);
export const ELLIOTT_TOOL = lineTool(
  "path", "Elliott wave", "arrowColor", "arrowWidth", "arrowStyle",
  "#2962FF", 3, "solid",
);

export const TEXT_TOOL: ToolDef = {
  id: "text",
  label: "Text",
  drawing: { stateKey: "textLabels", wireKey: "text_labels" },
  settings: {
    scope: { levels: "workspace-user", perMode: true },
    fields: [
      { kind: "color", key: "textLabelColor", label: "Text colour", default: "#E7EAF3" },
      { kind: "number", key: "textLabelFontSize", label: "Font size", min: 8, max: 48, unit: "px", default: 14 },
      { kind: "toggle", key: "textLabelBold", label: "Bold", default: false },
    ],
  },
};

/** All registered tools. Generic machinery iterates this. */
export const TOOLS: ToolDef[] = [
  PRICE_RANGE_TOOL,
  VERTICAL_TOOL,
  HORIZONTAL_TOOL,
  TREND_TOOL,
  ELLIOTT_TOOL,
  TEXT_TOOL,
];

export function toolById(id: string): ToolDef | undefined {
  return TOOLS.find((t) => t.id === id);
}
